// SearchableSelect.jsx - 通用可搜索下拉选择器组件
// 支持从预定义列表中选择，也允许用户自定义输入
// 参考 MajorSelector 实现，但允许自由输入

import React, { useState, useRef, useEffect } from 'react';

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Search or type...',
  disabled = false,
  className = '',
  inputClassName = '',
  onKeyDown: customOnKeyDown = null,
  onEnterSave,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // 同步外部 value 变化
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // 过滤选项
  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    setSelectedIndex(-1);
  }, [inputValue, options]);

  // 处理输入变化
  const handleInputChange = e => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowDropdown(true);
    // 立即通知父组件（允许自定义输入）
    if (onChange) {
      onChange(newValue);
    }
  };

  // 处理键盘导航
  const handleKeyDown = e => {
    // 如果自定义了 onKeyDown，先执行它
    if (customOnKeyDown) {
      customOnKeyDown(e);
    }

    // 回车键处理
    if (e.key === 'Enter') {
      if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
        e.preventDefault();
        handleSelectOption(filteredOptions[selectedIndex]);
        // 如果有 onEnterSave，执行它
        if (onEnterSave) {
          onEnterSave();
        }
      } else if (onEnterSave) {
        // 即使没有选择选项，也允许保存（自定义输入）
        onEnterSave();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showDropdown && filteredOptions.length > 0) {
        setShowDropdown(true);
        setSelectedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showDropdown && filteredOptions.length > 0) {
        setShowDropdown(true);
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  };

  // 选择选项
  const handleSelectOption = option => {
    setInputValue(option);
    setShowDropdown(false);
    setSelectedIndex(-1);
    if (onChange) {
      onChange(option);
    }
    // 选择后聚焦输入框（可选）
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type='text'
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed pr-10 ${inputClassName}`}
      />

      {/* 下拉箭头 */}
      {options.length > 0 && (
        <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
          <svg
            className='w-4 h-4 text-gray-400'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M19 9l-7 7-7-7'
            />
          </svg>
        </div>
      )}

      {/* 下拉选项 */}
      {showDropdown && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className='absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto'
        >
          {filteredOptions.map((option, index) => (
            <div
              key={option}
              className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-purple-50 ${
                index === selectedIndex ? 'bg-purple-100' : ''
              }`}
              onClick={() => handleSelectOption(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
